/**
 * Login Component
 * 
 * Provides a user interface for logging in with email and password.
 * Handles form submission, error display, and redirects to dashboard on successful login.
 * 
 * Features:
 * - Email and password input validation
 * - Error message display
 * - Loading state during authentication
 * - Link to registration page
 */

import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { loginUser } from '../utilities/authUtils';
import AppLogo from '../../Common/Brand/AppLogo';
import FeedbackNotice from '../../Common/Feedback/FeedbackNotice';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectPath = new URLSearchParams(location.search).get('redirectPath') || '';

  const getTargetPath = () => {
    if (!redirectPath) {
      return '/dashboard';
    }

    return redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
  };

  /**
   * Handle form submission for user login
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate input
      if (!email || !password) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      // Attempt login
      const result = await loginUser(email, password);
      
      if (result.success) {
        // Store user info in localStorage for persistence
        localStorage.setItem('user', JSON.stringify({
          uid: result.uid,
          email: result.email,
        }));
        
        // Redirect to the requested path, or dashboard by default
        navigate(getTargetPath(), { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container align-content-center" style={{height: "100vh"}}>
      <div className="card p-4 m-auto" style={{ maxWidth: '400px'}}>
        <AppLogo className="auth-brand" size={80} linked />
        <h2 className="text-center">Login</h2>
        
        <FeedbackNotice message={error || ''} variant="danger" onDismiss={() => setError(null)} />
        
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="email" className="form-label">Email Address</label>
            <input
              type="email"
              className="form-control"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              className="form-control"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="mt-3 text-center">
          Don't have an account?{' '}
          <Link to="/register" className="auth-link">
            Register here
          </Link>
        </p>

        <p className="text-center mb-0 small text-muted">
          <Link to="/privacy-policy" className="text-decoration-none">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
